import { Component, OnInit, ChangeDetectorRef, ChangeDetectionStrategy } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { AuthService } from '../_services/auth-service.service';
import { FamilyService } from '../_services/family.service';
import { Router } from '@angular/router';
import { TokenStorageService } from '../_services/token-storage.service';

type View = 'members' | 'pin' | 'add-member';

@Component({
  selector: 'app-login',
  templateUrl: './login.component.html',
  styleUrls: ['./login.component.css'],
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [FormsModule]
})
export class LoginComponent implements OnInit {
  view: View = 'members';

  familyName = '';
  members: { id: number; name: string }[] = [];
  membersLoading = true;
  membersError = '';

  selectedUser: { id: number; name: string } | null = null;
  inputCode: number[] = [];
  statusText = '';
  statusClass = '';

  pinRequestLoading = false;
  pinRequestStatus = '';
  pinRequestIsError = false;

  newUsername = '';
  newEmail = '';
  newPin = '';
  newShowPin = false;
  addMemberLoading = false;
  addMemberStatus = '';
  addMemberIsError = false;

  constructor(
    private authService: AuthService,
    private familyService: FamilyService,
    private tokenService: TokenStorageService,
    private router: Router,
    private cdr: ChangeDetectorRef
  ) {}

  ngOnInit(): void {
    this.authService.checkLoggedIn().subscribe(user => {
      if (user.success) { this.router.navigate(['/list']); }
    });

    const familyId = this.familyService.getFamilyId();
    if (!familyId) {
      this.router.navigate(['/family']);
      return;
    }

    this.familyName = this.familyService.getFamilyName();
    this.familyService.getMembers(familyId).subscribe({
      next: (data) => {
        this.members = JSON.parse(data);
        this.membersLoading = false;
        this.cdr.detectChanges();
      },
      error: () => {
        this.membersError = 'Kon leden niet laden. Controleer je verbinding.';
        this.membersLoading = false;
        this.cdr.detectChanges();
      }
    });

    this.initInstallPrompt();
  }

  selectMember(member: { id: number; name: string }): void {
    this.selectedUser = member;
    this.inputCode = [];
    this.statusText = '';
    this.statusClass = '';
    this.view = 'pin';
  }

  backToMembers(): void {
    this.view = 'members';
    this.selectedUser = null;
    this.inputCode = [];
    this.statusText = '';
    this.pinRequestStatus = '';
    this.pinRequestIsError = false;
    this.addMemberStatus = '';
    this.addMemberIsError = false;
  }

  showAddMember(): void {
    this.newUsername = '';
    this.newEmail = '';
    this.newPin = '';
    this.newShowPin = false;
    this.addMemberStatus = '';
    this.addMemberIsError = false;
    this.view = 'add-member';
  }

  submitAddMember(): void {
    if (this.addMemberLoading || !this.newUsername.trim() || !this.newPin.trim()) return;
    const familyId = this.familyService.getFamilyId();
    if (!familyId) return;

    this.addMemberLoading = true;
    this.addMemberStatus = '';
    this.authService.addMember({
      subscription_id: familyId,
      username: this.newUsername.trim(),
      email: this.newEmail.trim(),
      password: this.newPin.trim(),
    }).subscribe({
      next: (raw) => {
        const res = JSON.parse(raw);
        this.addMemberIsError = !res.success;
        this.addMemberStatus = res.message ?? (res.success ? 'Gebruiker aangemaakt!' : 'Mislukt.');
        this.addMemberLoading = false;
        if (res.success) {
          this.familyService.getMembers(familyId).subscribe({
            next: (data) => {
              this.members = JSON.parse(data);
              this.cdr.markForCheck();
            }
          });
        }
        this.cdr.markForCheck();
      },
      error: () => {
        this.addMemberIsError = true;
        this.addMemberStatus = 'Kon de server niet bereiken.';
        this.addMemberLoading = false;
        this.cdr.markForCheck();
      }
    });
  }

  requestPin(): void {
    if (this.pinRequestLoading || !this.selectedUser) return;
    this.pinRequestLoading = true;
    this.pinRequestStatus = '';
    this.authService.requestPin(this.selectedUser.id).subscribe({
      next: (raw) => {
        const res = JSON.parse(raw);
        this.pinRequestIsError = !res.success;
        this.pinRequestStatus = res.message ?? (res.success ? 'Pincode verzonden!' : 'Er ging iets mis.');
        this.pinRequestLoading = false;
        this.cdr.markForCheck();
      },
      error: () => {
        this.pinRequestIsError = true;
        this.pinRequestStatus = 'Kon de server niet bereiken.';
        this.pinRequestLoading = false;
        this.cdr.markForCheck();
      }
    });
  }

  changeFamily(): void {
    this.familyService.clearFamily();
    this.router.navigate(['/family']);
  }

  add(number: number): void {
    if (this.inputCode.length < 5) {
      this.inputCode.push(number);
    }
    if (this.inputCode.length >= 5) {
      this.statusClass = 'status-notice';
      this.statusText = 'Bezig met inloggen…';
      this.onCodeCompleted(this.inputCode.join(''));
    }
  }

  del(): void {
    this.inputCode = this.inputCode.slice(0, -1);
  }

  submitCode(): void {
    if (this.inputCode.length === 5) {
      this.onCodeCompleted(this.inputCode.join(''));
    }
  }

  onCodeCompleted(code: string): void {
    this.inputCode = [];
    this.authService.loginWithPin(code, this.selectedUser!.id).subscribe({
      next: () => { this.router.navigate(['/list']); },
      error: () => {
        this.statusClass = 'status-error';
        this.statusText = 'Pincode onjuist. Probeer opnieuw.';
      }
    });
  }

  private initInstallPrompt(): void {
    let installPrompt: any = null;
    const installButton = document.querySelector('#install');
    if (!installButton) return;

    window.addEventListener('beforeinstallprompt', (event) => {
      event.preventDefault();
      installPrompt = event;
      installButton.removeAttribute('hidden');
    });
    installButton.addEventListener('click', async () => {
      if (!installPrompt) return;
      await installPrompt.prompt();
      installPrompt = null;
      installButton.setAttribute('hidden', '');
    });
    window.addEventListener('appinstalled', () => {
      installPrompt = null;
      installButton.setAttribute('hidden', '');
    });
  }
}
